import { cleanAPI } from './config';
import { AUTH_REFRESH_URI } from '@/constants';
import { saveToken, updateBearerToken } from '@/stores/secure';

export interface RefreshTokenResponse {
  jwt: string;
  refreshToken?: string;
}

const MAX_RETRY_ATTEMPTS = 3;
const INITIAL_RETRY_DELAY_MS = 1000;
const MAX_RETRY_DELAY_MS = 10000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getRetryDelay = (attempt: number): number => {
  const exp = Math.min(INITIAL_RETRY_DELAY_MS * 2 ** attempt, MAX_RETRY_DELAY_MS);
  const jitter = Math.random() * 0.3 * exp; // avoid thundering herd
  return exp + jitter;
};

interface RetryableError {
  response?: { status?: number };
  code?: string;
  message?: string;
}

const isRetryableError = (error: unknown): boolean => {
  const err = error as RetryableError;
  if (!err?.response && (err?.code === 'ECONNABORTED' || err?.message?.includes('Network'))) {
    return true;
  }
  const status = err?.response?.status;
  if (status !== undefined && status >= 500 && status < 600) return true;
  if (status === 408) return true;
  return false;
};

/**
 * Refreshes the access token. Uses `cleanAPI` (no interceptor) to avoid a
 * circular dependency with the main API 401 handler. Retries network / 5xx
 * errors with exponential backoff + jitter. Ported from the legacy app.
 */
export const refreshAccessToken = async (
  refreshToken: string,
  attempt = 0,
): Promise<RefreshTokenResponse> => {
  try {
    const response = await cleanAPI.post(AUTH_REFRESH_URI, { refreshToken }, { timeout: 15000 });

    const jwt = response.data?.jwt || response.data?.access_token || response.data?.token;
    const newRefreshToken = response.data?.refreshToken || response.data?.refresh_token;

    if (!jwt) {
      throw new Error('Invalid refresh token response: missing jwt');
    }

    return { jwt, refreshToken: newRefreshToken };
  } catch (error) {
    const canRetry = attempt < MAX_RETRY_ATTEMPTS && isRetryableError(error);
    if (canRetry) {
      await sleep(getRetryDelay(attempt));
      return refreshAccessToken(refreshToken, attempt + 1);
    }
    throw error;
  }
};

/**
 * Refreshes AND persists both tokens. Strapi rotates the refresh token on every
 * call: keeping the old one means re-sending the login token forever, which
 * Strapi resolves to a child that expires 30 days after its creation — users
 * were logged out about once a month. Every refresh path must go through here.
 */
export const refreshAndStoreTokens = async (refreshToken: string): Promise<string> => {
  const { jwt, refreshToken: rotated } = await refreshAccessToken(refreshToken);
  if (rotated) await saveToken(jwt, rotated);
  else await updateBearerToken(jwt);
  return jwt;
};
